import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Container, Form, Spinner, Table } from "react-bootstrap";
import apiClient from "@shared/services/apiClient";
import { useAuth } from "@shared/context/AuthContext";

type VendorRecord = {
  vendor_id: string;
  vendor_name: string;
  formats: string[];
  parser_ids: string[];
  support_level: string;
  upload_agent_live_path: boolean;
  golden_file_id: string | null;
  limitations: string[];
  evidence_uri: string | null;
  evidence_sha256: string | null;
  verification_status: string;
  last_pass_at: string | null;
  version: number;
};

type VendorDraft = {
  vendorId: string;
  vendorName: string;
  formats: string;
  parserIds: string;
  supportLevel: string;
  uploadAgentLivePath: boolean;
  goldenFileId: string;
  limitations: string;
  evidenceUri: string;
  evidenceSha256: string;
  verificationStatus: string;
  lastPassAt: string;
  version: number;
};

const blankDraft: VendorDraft = {
  vendorId: "", vendorName: "", formats: "", parserIds: "", supportLevel: "unverified",
  uploadAgentLivePath: false, goldenFileId: "", limitations: "", evidenceUri: "",
  evidenceSha256: "", verificationStatus: "not_run", lastPassAt: "", version: 0,
};

function toDraft(row: VendorRecord): VendorDraft {
  return {
    vendorId: row.vendor_id, vendorName: row.vendor_name,
    formats: row.formats.join(", "), parserIds: row.parser_ids.join(", "),
    supportLevel: row.support_level, uploadAgentLivePath: row.upload_agent_live_path,
    goldenFileId: row.golden_file_id || "", limitations: row.limitations.join("\n"),
    evidenceUri: row.evidence_uri || "", evidenceSha256: row.evidence_sha256 || "",
    verificationStatus: row.verification_status, lastPassAt: row.last_pass_at || "",
    version: row.version,
  };
}

function splitValues(value: string) {
  return value.split(",").map((item) => item.trim()).filter(Boolean);
}

export default function VendorSupportMatrixPanel() {
  const { user } = useAuth();
  const canManage = user?.role === "SUPER_ADMIN";
  const [records, setRecords] = useState<VendorRecord[]>([]);
  const [draft, setDraft] = useState<VendorDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiClient.get("/vendor-support");
      const rows = response.data?.data;
      if (!Array.isArray(rows)) throw new Error("Invalid vendor support response");
      setRecords(rows as VendorRecord[]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not load vendor support evidence");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError("");
    try {
      await apiClient.put(`/vendor-support/${encodeURIComponent(draft.vendorId)}`, {
        vendorName: draft.vendorName.trim(),
        formats: splitValues(draft.formats),
        parserIds: splitValues(draft.parserIds),
        supportLevel: draft.supportLevel,
        uploadAgentLivePath: draft.uploadAgentLivePath,
        goldenFileId: draft.goldenFileId.trim() || null,
        limitations: draft.limitations.split("\n").map((line) => line.trim()).filter(Boolean),
        evidenceUri: draft.evidenceUri.trim() || null,
        evidenceSha256: draft.evidenceSha256.trim().toLowerCase() || null,
        verificationStatus: draft.verificationStatus,
        lastPassAt: draft.lastPassAt ? new Date(draft.lastPassAt).toISOString() : null,
        version: draft.version,
      });
      setDraft(null);
      await load();
    } catch (cause: any) {
      setError(cause?.response?.data?.error || cause?.message || "Could not save vendor evidence");
    } finally {
      setSaving(false);
    }
  };

  return <Container fluid className="py-3" data-testid="vendor-support-matrix-panel">
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div><h1 className="mb-1">Vendor Support</h1><p className="text-muted mb-0">Evidence-backed interoperability status from the database.</p></div>
      {canManage && <Button onClick={() => setDraft({ ...blankDraft })}>Add vendor evidence</Button>}
    </div>
    {error && <Alert variant="danger" role="alert">{error}</Alert>}
    {loading ? <Spinner animation="border" role="status" /> : records.length === 0 ?
      <Alert variant="info">No verified vendor support records have been published.</Alert> :
      <Table responsive hover size="sm" data-testid="vendor-matrix-table">
        <thead><tr><th>Vendor</th><th>Formats</th><th>Support</th><th>Validation</th><th>Last pass</th><th>Evidence</th><th>Limitations</th>{canManage && <th>Action</th>}</tr></thead>
        <tbody>{records.map((row) => {
          const verified = row.verification_status === "passed" && Boolean(row.evidence_uri && row.evidence_sha256 && row.last_pass_at);
          const level = verified || ["planned", "unsupported"].includes(row.support_level) ? row.support_level : "unverified";
          return <tr key={row.vendor_id}>
            <td>{row.vendor_name}</td><td>{row.formats.join(", ") || "—"}</td>
            <td><Badge bg={level === "certified" ? "success" : level === "best_effort" ? "warning" : "secondary"}>{level}</Badge></td>
            <td>{verified ? "Passed" : row.verification_status === "failed" ? "Failed" : "Not verified"}</td>
            <td>{verified ? new Date(row.last_pass_at!).toLocaleDateString() : "—"}</td>
            <td>{verified && row.evidence_uri ? <a href={row.evidence_uri} target="_blank" rel="noopener noreferrer">Evidence</a> : "—"}</td>
            <td>{row.limitations.join("; ") || "—"}</td>
            {canManage && <td><Button size="sm" variant="outline-primary" onClick={() => setDraft(toDraft(row))}>Edit</Button></td>}
          </tr>;
        })}</tbody>
      </Table>}
    {canManage && draft && <Card className="mt-3"><Card.Body>
      <h2 className="h5">{draft.version ? "Edit vendor evidence" : "New vendor evidence"}</h2>
      <Alert variant="warning">Certified and best-effort claims require a real passing test and immutable evidence reference. Do not enter sample results.</Alert>
      <Form onSubmit={(event) => { event.preventDefault(); void save(); }}>
        <div className="row g-3">
          <Form.Group className="col-md-4"><Form.Label>Vendor ID</Form.Label><Form.Control required pattern="[a-z0-9][a-z0-9-]{1,79}" value={draft.vendorId} disabled={draft.version > 0} onChange={(event) => setDraft({ ...draft, vendorId: event.target.value })} /></Form.Group>
          <Form.Group className="col-md-8"><Form.Label>Vendor name</Form.Label><Form.Control required value={draft.vendorName} onChange={(event) => setDraft({ ...draft, vendorName: event.target.value })} /></Form.Group>
          <Form.Group className="col-md-6"><Form.Label>Formats (comma-separated)</Form.Label><Form.Control value={draft.formats} onChange={(event) => setDraft({ ...draft, formats: event.target.value })} /></Form.Group>
          <Form.Group className="col-md-6"><Form.Label>Parser IDs (comma-separated)</Form.Label><Form.Control value={draft.parserIds} onChange={(event) => setDraft({ ...draft, parserIds: event.target.value })} /></Form.Group>
          <Form.Group className="col-md-4"><Form.Label>Support level</Form.Label><Form.Select value={draft.supportLevel} onChange={(event) => setDraft({ ...draft, supportLevel: event.target.value })}>{["unverified", "certified", "best_effort", "unsupported", "planned"].map((level) => <option key={level}>{level}</option>)}</Form.Select></Form.Group>
          <Form.Group className="col-md-4"><Form.Label>Verification</Form.Label><Form.Select value={draft.verificationStatus} onChange={(event) => setDraft({ ...draft, verificationStatus: event.target.value })}>{["not_run", "passed", "failed"].map((status) => <option key={status}>{status}</option>)}</Form.Select></Form.Group>
          <Form.Group className="col-md-4"><Form.Label>Last passing test (UTC)</Form.Label><Form.Control type="datetime-local" value={draft.lastPassAt ? draft.lastPassAt.slice(0, 16) : ""} onChange={(event) => setDraft({ ...draft, lastPassAt: event.target.value })} /></Form.Group>
          <Form.Group className="col-md-6"><Form.Label>Golden-file ID</Form.Label><Form.Control value={draft.goldenFileId} onChange={(event) => setDraft({ ...draft, goldenFileId: event.target.value })} /></Form.Group>
          <Form.Group className="col-md-6"><Form.Label>Evidence URL</Form.Label><Form.Control type="url" value={draft.evidenceUri} onChange={(event) => setDraft({ ...draft, evidenceUri: event.target.value })} /></Form.Group>
          <Form.Group className="col-md-12"><Form.Label>Evidence SHA-256</Form.Label><Form.Control pattern="[a-fA-F0-9]{64}" value={draft.evidenceSha256} onChange={(event) => setDraft({ ...draft, evidenceSha256: event.target.value })} /></Form.Group>
          <Form.Group className="col-md-12"><Form.Label>Limitations (one per line)</Form.Label><Form.Control as="textarea" rows={2} value={draft.limitations} onChange={(event) => setDraft({ ...draft, limitations: event.target.value })} /></Form.Group>
          <Form.Group className="col-md-12"><Form.Check type="switch" label="Upload agent live path verified" checked={draft.uploadAgentLivePath} onChange={(event) => setDraft({ ...draft, uploadAgentLivePath: event.target.checked })} /></Form.Group>
        </div>
        <div className="d-flex gap-2 mt-3"><Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save evidence"}</Button><Button variant="outline-secondary" onClick={() => setDraft(null)}>Cancel</Button></div>
      </Form>
    </Card.Body></Card>}
  </Container>;
}
